import React, { useState } from 'react';
import { Form, Button, Table, Alert, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
  useCreateExpenseMutation,
  useUploadExpenseImagesMutation,
} from '../../slices/expensesApiSlice';
import { useGetCustomersQuery } from '../../slices/customersApiSlice';

const CATEGORY_OPTIONS = [
  'Consult', 'SalesCredit', 'SalesPayment', 'supplyPayment',
  'Service', 'Lab', 'Electricity', 'Travel', 'Food',
  'Office Supplies', 'Other', 'Observation', 'Admission',
  'Dressing', 'Delivery', 'Expense'
];

const AddExpenseFromExcelScreen = () => {
  const [rows, setRows] = useState([]);
  const [results, setResults] = useState([]);
  //const [fileName, setFileName] = useState('');
  const [globalDate, setGlobalDate] = useState('');
  const [createExpense, { isLoading }] = useCreateExpenseMutation();
  const [uploadExpenseImages] = useUploadExpenseImagesMutation();
  const { data: customerData } = useGetCustomersQuery({ keyword: '' });
  const customers = customerData?.customers || [];

  const isValidDate = (d) => !isNaN(new Date(d).getTime());

  const getClosestCustomer = (txt) => {
    if (!txt) return null;
    const norm = txt.trim().toLowerCase();
    let best = null, score = Infinity;
    customers.forEach((c) => {
      const name = c.name.trim().toLowerCase();
      const diff =
        Math.abs(name.length - norm.length) +
        [...name].filter((ch, i) => ch !== norm[i]).length;
      if (diff < score) {
        score = diff;
        best = c;
      }
    });
    return score <= 5 ? best : null;
  };

  const validateRow = (r) => {
    if (!isValidDate(r.date)) return 'Invalid date';
    if (r.account === 'Credit' && +r.amount >= 0) return 'Credit must be negative';
    if (r.account === 'Debit' && +r.amount <= 0) return 'Debit must be positive';
    if (!r.accountName) return 'Missing accountName';
    if (!r.matchedCustomer) return 'AccountName not found';
    if (!CATEGORY_OPTIONS.includes(r.category)) return 'Invalid category';
    return null;
  };

  const validateFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File type not allowed: ${file.type}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File size exceeds 5MB: ${file.name}`);
        return false;
      }
    }
    return true;
  };

  const updateRow = (index, fields) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...fields } : row))
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    //setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'array' });
      const sh = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sh, { defval: '' });
      const today = new Date().toISOString().split('T')[0];

      const enriched = data.map((row) => {
        const match = getClosestCustomer(row.AccountName);
        return {
          date: isValidDate(row.Date) ? row.Date : today,
          account: row.Account || '',
          amount: row.Amount || '',
          accountName: match?.name || row.AccountName || '',
          matchedCustomer: match,
          category: row.Category || '',
          details: row.Details || '',
          notes: row.Notes || '',
          images: [],
        };
      });

      setRows(enriched);
      setResults([]);
      toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImageUpload = async (rowIndex, files) => {
    if (!validateFiles(files)) return;

    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));

    try {
      const res = await uploadExpenseImages(formData).unwrap();
      toast.success(res.message);
      const updatedImages = [...rows[rowIndex].images, ...res.images];
      updateRow(rowIndex, { images: updatedImages });
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const handleImageDrop = (e, i) => {
    e.preventDefault();
    handleImageUpload(i, Array.from(e.dataTransfer.files));
  };

  const handleImageSelect = (e, i) => {
    handleImageUpload(i, Array.from(e.target.files));
  };

  const removeImage = (rowIndex, imgIndex) => {
    const imgs = [...rows[rowIndex].images];
    imgs.splice(imgIndex, 1);
    updateRow(rowIndex, { images: imgs });
  };

  const applyGlobalDate = () => {
    if (!isValidDate(globalDate)) return toast.error('Invalid global date');
    setRows((prev) => prev.map((r) => ({ ...r, date: globalDate })));
    toast.info(`Applied ${globalDate} to all rows`);
  };

  const handleSubmit = async () => {
    const feedback = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const err = validateRow(r);
      if (err) {
        feedback.push({
          index: i + 1,
          status: 'Skipped',
          message: err,
        });
        continue;
      }
      try {
        await createExpense({
          date: r.date,
          account: r.account,
          accountName: r.accountName,
          customerId: r.matchedCustomer._id,
          details: r.details,
          amount: +r.amount,
          category: r.category,
          notes: r.notes,
          images: r.images,
        }).unwrap();
        feedback.push({ index: i + 1, status: 'Success', message: 'Uploaded' });
      } catch (err) {
        feedback.push({
          index: i + 1,
          status: 'Failed',
          message: err?.data?.message || err.message,
        });
      }
    }
    setResults(feedback);
    toast.success('Upload completed');
  };

  //── Return Block ─────────────────────────────────────────────────────
  return (
    <div className="container mt-4">
      <h2>📥 Add Expenses from Excel</h2>

      <Form.Group controlId="excelUpload" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx" onChange={handleFileUpload} />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Global Date</Form.Label>
        <div className="d-flex">
          <Form.Control
            type="date"
            value={globalDate}
            onChange={(e) => setGlobalDate(e.target.value)}
          />
          <Button variant="secondary" onClick={applyGlobalDate} className="ms-2">
            Apply to All
          </Button>
        </div>
      </Form.Group>

      {rows.length > 0 && (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th><th>Date</th><th>Account</th><th>Account Name</th>
                <th>Amount</th><th>Category</th><th>Details</th><th>Notes</th>
                <th>Images</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ background: validateRow(r) ? '#fff3cd' : 'inherit' }}>
                  <td>{i + 1}</td>
                  <td><Form.Control type="date" value={r.date} onChange={(e) => updateRow(i, { date: e.target.value })} /></td>
                  <td>
                    <Form.Select value={r.account} onChange={(e) => updateRow(i, { account: e.target.value })}></Form.Select>

                    <Form.Select value={r.account} onChange={(e) => updateRow(i, { account: e.target.value })}>
  <option value="">Select</option>
  <option value="Debit">Debit</option>
  <option value="Credit">Credit</option>
</Form.Select>
</td>
<td>
  <Form.Control
    type="text"
    value={r.accountName}
    onChange={(e) => updateRow(i, { accountName: e.target.value })}
  />
</td>
<td>
  <Form.Control
    type="number"
    value={r.amount}
    onChange={(e) => updateRow(i, { amount: e.target.value })}
  />
</td>
<td>
  <Form.Select
    value={r.category}
    onChange={(e) => updateRow(i, { category: e.target.value })}
  >
    <option value="">Select</option>
    {CATEGORY_OPTIONS.map((cat) => (
      <option key={cat} value={cat}>{cat}</option>
    ))}
  </Form.Select>
</td>
<td>
  <Form.Control
    type="text"
    value={r.details}
    onChange={(e) => updateRow(i, { details: e.target.value })}
  />
</td>
<td>
  <Form.Control
    type="text"
    value={r.notes}
    onChange={(e) => updateRow(i, { notes: e.target.value })}
  />
</td>
<td
  onDrop={(e) => handleImageDrop(e, i)}
  onDragOver={(e) => e.preventDefault()}
  style={{ minWidth: '200px' }}
>
  <div className="d-flex flex-wrap gap-2">
    {r.images.map((img, idx) => (
      <div key={idx} className="position-relative">
        <img
          src={img.url}
          alt={`img-${idx}`}
          style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
        />
        <Button
          variant="danger"
          size="sm"
          onClick={() => removeImage(i, idx)}
          style={{ position: 'absolute', top: '-5px', right: '-5px', borderRadius: '50%' }}
        >
          ×
        </Button>
      </div>
    ))}
    <Form.Control
      type="file"
      multiple
      accept="image/jpeg,image/png,image/jpg"
      onChange={(e) => handleImageSelect(e, i)}
      style={{ width: '120px' }}
    />
  </div>
</td>
<td>
  {validateRow(r) ? (
    <Alert variant="warning" className="p-1">{validateRow(r)}</Alert>
  ) : (
    <span className="text-success">✓</span>
  )}
</td>
</tr>
))}
</tbody>
</Table>

<Button
  variant="primary"
  onClick={handleSubmit}
  disabled={isLoading}
>
  {isLoading ? <Spinner animation="border" size="sm" /> : 'Submit Expenses'}
</Button>

{results.length > 0 && (
  <div className="mt-4">
    <h5>📊 Submission Results</h5>
    <Table striped bordered>
      <thead>
        <tr>
          <th>#</th><th>Status</th><th>Message</th>
        </tr>
      </thead>
      <tbody>
        {results.map((r, i) => (
          <tr key={i}>
            <td>{r.index}</td>
            <td>{r.status}</td>
            <td>{r.message}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
)}
</>
)}
</div>
);
};

export default AddExpenseFromExcelScreen;